// Account-aware date formatting (LIFF-H5).
//
// Mirror of web/src/lib/date.ts: on-screen dates must honour the account's
// configured LAYOUT (date_format), ZONE (timezone), and ERA (calendar:
// Gregorian or Buddhist) — not a hardcoded toLocaleDateString('th-TH').
// Pages read these via useDateFormatter (bound to the resolved account);
// this module holds the pure formatting so it's unit-testable.

export type DateFormatPattern = 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD'
export type CalendarSystem = 'gregorian' | 'buddhist'

export const DEFAULT_DATE_FORMAT: DateFormatPattern = 'DD/MM/YYYY'
export const DEFAULT_TIMEZONE = 'Asia/Bangkok'
export const DEFAULT_CALENDAR: CalendarSystem = 'gregorian'

export interface DateFormatOptions {
  /** Layout pattern. Defaults to DD/MM/YYYY. */
  format?: string
  /** IANA timezone (e.g. 'Asia/Bangkok'). Defaults to the runtime zone. */
  timezone?: string
  /** 'gregorian' (CE) or 'buddhist' (BE, +543). Defaults to gregorian. */
  calendar?: CalendarSystem
}

// Extract day / month / year parts of a Date in the requested timezone +
// calendar, using Intl so era + zone are correct.
function partsOf(d: Date, timezone?: string, calendar?: CalendarSystem, withTime = false) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    ...(timezone ? { timeZone: timezone } : {}),
    ...(calendar === 'buddhist' ? { calendar: 'buddhist' } : {}),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(withTime ? { hour: '2-digit', minute: '2-digit', hour12: false } : {}),
  })
  const parts = dtf.formatToParts(d)
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  return {
    dd: get('day'),
    mm: get('month'),
    // Intl pads the Gregorian year to 4 digits; the Buddhist year is already
    // 4 digits (2569). Normalise any stray padding off the front.
    yyyy: get('year').replace(/^0+(?=\d)/, ''),
    hh: withTime ? (get('hour') === '24' ? '00' : get('hour')) : undefined,
    min: withTime ? get('minute') : undefined,
  }
}

function layout(format: string, dd: string, mm: string, yyyy: string): string {
  switch (format) {
    case 'MM/DD/YYYY':
      return `${mm}/${dd}/${yyyy}`
    case 'YYYY-MM-DD':
      return `${yyyy}-${mm}-${dd}`
    case 'DD/MM/YYYY':
    default:
      return `${dd}/${mm}/${yyyy}`
  }
}

/**
 * Format an ISO date string per the account's layout / zone / calendar.
 * Returns '' for null/empty/invalid input so callers can render it directly.
 */
export function formatDate(
  dateString: string | null | undefined,
  options: DateFormatOptions = {},
): string {
  if (!dateString) return ''
  const d = new Date(dateString)
  if (Number.isNaN(d.getTime())) return ''
  const { format = DEFAULT_DATE_FORMAT, timezone, calendar } = options
  const { dd, mm, yyyy } = partsOf(d, timezone, calendar)
  return layout(format, dd, mm, yyyy)
}

/**
 * Like formatDate but appends 24-hour HH:MM in the account timezone. Used
 * for audit-style datetime fields (meeting_at, signed_at). Mirrors the
 * web-side `formatDateTime`.
 */
export function formatDateTime(
  dateString: string | null | undefined,
  options: DateFormatOptions = {},
): string {
  if (!dateString) return ''
  const d = new Date(dateString)
  if (Number.isNaN(d.getTime())) return ''
  const { format = DEFAULT_DATE_FORMAT, timezone, calendar } = options
  const { dd, mm, yyyy, hh, min } = partsOf(d, timezone, calendar, true)
  return `${layout(format, dd, mm, yyyy)} ${hh}:${min}`
}

export interface MonthYearOptions {
  /** BCP-47 locale for the month name (e.g. 'en', 'th'). Defaults to 'th'. */
  locale?: string
  /** 'gregorian' (CE) or 'buddhist' (BE, +543). Defaults to gregorian. */
  calendar?: CalendarSystem
}

/**
 * Format a short-month + year label (e.g. "Jun 2026" / "มิ.ย. 2569") honouring
 * the account's calendar era and the UI locale. Used for the invoice
 * billing-period labels.
 */
export function formatMonthYear(
  dateString: string | null | undefined,
  options: MonthYearOptions = {},
): string {
  if (!dateString) return ''
  const d = new Date(dateString)
  if (Number.isNaN(d.getTime())) return ''
  const { locale = 'th', calendar } = options
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    year: 'numeric',
    // Always named: left out, the Thai locale falls back to the Buddhist era
    // (CLDR's default for th), so a Gregorian account read "ก.ย. 2569".
    calendar: calendar === 'buddhist' ? 'buddhist' : 'gregory',
  }).format(d)
}
